import React, { useState, useRef, useEffect } from 'react';

export function MultiSelectDropdown({ label, options, selected, setSelected, className = '', style = {}, enableSearch = true, showLabelWithSelected = false }) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const searchInputRef = useRef(null);
	const dropdownRef = useRef(null);

	useEffect(() => {
		if(!open) return;
		const handleClick = (e) => {
			if(dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				setOpen(false);
				setSearch('');
			}
		};
		document.addEventListener('mousedown', handleClick);
		return () => document.removeEventListener('mousedown', handleClick);
	}, [open]);

	const handleButtonDoubleClick = () => {
		setOpen(true);
		setTimeout(() => {
			if(searchInputRef.current) searchInputRef.current.focus();
		}, 0);
	};

	const toggleOption = (id) => {
		setSelected((prev) =>
			prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
		);
	};
	const selectedObjs = options.filter((o) => selected.includes(o.id || o));
	const selectedDisplay = selectedObjs.map((o) => o.name || o.material || o).join(', ');
	const display = showLabelWithSelected
		? (selected.length === 0 ? label : `${label}: ${selectedDisplay}`)
		: (selected.length === 0 ? `${label}` : selectedDisplay);

	const filteredOptions = search.trim() === ''
		? options
		: options.filter((o) => (o.name || o.material || o).toLowerCase().includes(search.toLowerCase()));

	return (
		<div className={`relative min-w-[140px] max-w-[180px] sm:min-w-[180px] sm:max-w-[180px] w-full ${className}`} style={style} ref={dropdownRef}>
			<button
				type='button'
				className='w-full bg-black/80 text-white px-4 py-2 rounded-full shadow border border-gray-800 hover:bg-[#121212] hover:border-gray-700 transition flex justify-between items-center custom-focus-outline'
				onClick={() => {
					if(open) {
						setOpen(false);
						setSearch('');
						if(document.activeElement) document.activeElement.blur();
					} else {
						setOpen(true);
					}
				}}
				onDoubleClick={handleButtonDoubleClick}
			>
				<span className='truncate'>{display || `${label}:`}</span>
				<svg
					className={`w-4 h-4 ml-2 transition-transform ${open ? 'rotate-180' : ''}`}
					fill='none'
					stroke='currentColor'
					strokeWidth='2'
					viewBox='0 0 24 24'
					style={{ flexShrink: 0 }}
				>
					<path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
				</svg>
			</button>
			{open && (
				<div className='absolute z-20 mt-2 w-full bg-black/80 border border-gray-800 rounded-2xl shadow-lg max-h-64 overflow-auto animate-fadeIn' style={{ maxHeight: '16rem', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
					<style>{`
						[data-hide-scrollbar]::-webkit-scrollbar { display: none; }
						input[type="checkbox"].circle-checkbox {
							appearance: none;
							-webkit-appearance: none;
							width: 1.25rem;
							height: 1.25rem;
							border-radius: 9999px;
							border: 2px solid #444;
							background: #222;
							outline: none;
							transition: border-color 0.2s, background 0.2s;
							display: flex;
							align-items: center;
							justify-content: center;
							vertical-align: middle;
							position: relative;
						}
						input[type="checkbox"].circle-checkbox:checked {
							background: #444;
							border-color: #888;
						}
						input[type="checkbox"].circle-checkbox:checked::after {
							content: '';
							display: block;
							width: 0.5rem;
							height: 0.5rem;
							margin: auto;
							border-radius: 9999px;
							background: #fff;
							position: absolute;
							top: 0;
							left: 0;
							right: 0;
							bottom: 0;
						}
						button.custom-focus-outline:focus, button.custom-focus-outline:active {
							outline: 2px solid #374151 !important;
							outline-offset: -1px;
							box-shadow: none !important;
						}
						label.dropdown-option-hover, label:hover {
							background: rgba(18,18,18,0.7) !important;
						}
					`}</style>
					<div className='p-2 pb-0 bg-transparent'>
						{enableSearch && (
							<input
								type='text'
								ref={searchInputRef}
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								placeholder={`Search ${label.toLowerCase()}...`}
								className='w-full px-3 py-1.5 mb-2 rounded-lg bg-[#18181b] text-white border border-gray-700 focus:outline-none focus:ring-1 focus:ring-gray-700 text-sm placeholder-gray-400 transition'
								style={{fontSize:'0.95rem'}}
							/>
						)}
					</div>
					<div data-hide-scrollbar>
						{filteredOptions.length === 0 && (
							<p className='text-gray-400 px-4 py-2 text-sm'>No options found.</p>
						)}
						{filteredOptions.map((o) => (
							<label key={o.id || o} className='flex items-center gap-2 px-4 py-2 hover:bg-[#121212] cursor-pointer text-white rounded-lg transition min-h-[2.25rem]'>
								<input
									type='checkbox'
									checked={selected.includes(o.id || o)}
									onChange={() => toggleOption(o.id || o)}
									className='circle-checkbox flex-shrink-0'
								/>
								<span className='ml-1'>{o.name || o.material || o}</span>
							</label>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

export function SingleSelectDropdown({ label, options, selected, setSelected, className = '', style = {}, showLabelWithSelected = false, allowDeselect = false }) {
	const [open, setOpen] = useState(false);
	const dropdownRef = useRef(null);

	useEffect(() => {
		if(!open) return;
		const handleClick = (e) => {
			if(dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClick);
		return () => document.removeEventListener('mousedown', handleClick);
	}, [open]);


	const selectedObj = (selected == null || selected === '') ? null : options.find((o) => (o.id || o) === selected);
	const selectedDisplay = selectedObj?.name || selectedObj?.material || selected;
	const display = (selected == null || selected === '')
		? `${label}`
		: showLabelWithSelected
			? `${label}: ${selectedDisplay}`
			: selectedDisplay;

	const handleOptionClick = (optionId) => {
		if(allowDeselect && (selected == null || selected === '' ? false : selected === optionId)) {
			setSelected(null);
			setOpen(false);
		} else {
			setSelected(optionId);
			setOpen(false);
		}
	};

	return (
		<div className={`relative min-w-[24vw] max-w-[180px] sm:min-w-[180px] sm:max-w-[180px] w-full ${className}`} style={style} ref={dropdownRef}>
			<button
				type='button'
				className='w-full bg-black/80 text-white px-4 py-2 rounded-full shadow border border-gray-800 hover:bg-[#121212] hover:border-gray-700 transition flex justify-between items-center custom-focus-outline'
				onClick={() => {
					if(open) {
						setOpen(false);
						if(document.activeElement) document.activeElement.blur();
					} else {
						setOpen(true);
					}
				}}
			>
				<span className='truncate'>{display}</span>
				<svg className={`w-4 h-4 ml-2 transition-transform ${open ? 'rotate-180' : ''}`} fill='none' stroke='currentColor' strokeWidth='2' viewBox='0 0 24 24' style={{ flexShrink: 0 }}>
					<path strokeLinecap='round' strokeLinejoin='round' d='M19 9l-7 7-7-7' />
				</svg>
			</button>
			{open && (
				<div className='absolute z-20 mt-2 w-full bg-black/80 border border-gray-800 rounded-2xl shadow-lg max-h-64 overflow-auto animate-fadeIn' style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
					<style>{`
						[data-hide-scrollbar]::-webkit-scrollbar { display: none; }
						input[type="radio"].circle-radio {
							appearance: none;
							-webkit-appearance: none;
							width: 1.25rem;
							height: 1.25rem;
							border-radius: 9999px;
							border: 2px solid #444;
							background: #222;
							outline: none;
							transition: border-color 0.2s, background 0.2s;
							display: flex;
							align-items: center;
							justify-content: center;
							vertical-align: middle;
							position: relative;
						}
						input[type="radio"].circle-radio:checked {
							background: #444;
							border-color: #888;
						}
						input[type="radio"].circle-radio:checked::after {
							content: '';
							display: block;
							width: 0.5rem;
							height: 0.5rem;
							margin: auto;
							border-radius: 9999px;
							background: #fff;
							position: absolute;
							top: 0;
							left: 0;
							right: 0;
							bottom: 0;
						}
						button.custom-focus-outline:focus, button.custom-focus-outline:active {
							outline: 2px solid #374151 !important;
							outline-offset: -1px;
							box-shadow: none !important;
						}
						label.dropdown-option-hover, label:hover {
							background: rgba(18,18,18,0.7) !important;
						}
					`}</style>
					<div data-hide-scrollbar>
						{options.length === 0 && (
							<p className='text-gray-400 px-4 py-2 text-sm'>No options found.</p>
						)}
						{options.map((o) => (
							<label key={o.id || o} className='flex items-center gap-2 px-4 py-2 hover:bg-[#121212] cursor-pointer text-white rounded-lg transition min-h-[2.25rem]'>
								<input
									type='radio'
									className='circle-radio flex-shrink-0'
									checked={selected != null && selected !== '' && selected === (o.id || o)}
									onClick={() => handleOptionClick(o.id || o)}
									readOnly
								/>
								<span className='ml-1'>{o.name || o.material || o}</span>
							</label>
						))}
					</div>
				</div>
			)}
		</div>
	);
}
